import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RetailerSidebar from "./RetailerSidebar";
import VisualizerLinkCard from "./VisualizerLinkCard";
import IntegrateRoomViz from "./dashboard/IntegrateRoomViz";
import { getRetailerStats, getRetailerStock, getRetailerVisualizerLink } from "../../services/api";
import DigitalShowroomCard from "../DigitalShowroom/DigitalShowroomCard";
import colors from "../../theme/colors";
import { useRetailerStore } from "../../store/useRetailerStore"; // Import Zustand store

export default function RetailerDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stockProducts, setStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Get Zustand action & state
  const setRetailerData = useRetailerStore((state) => state.setRetailerData);
  const visualizerToken = useRetailerStore((state) => state.visualizerToken);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [statsResponse, stockResponse, visualizerResponse] = await Promise.all([
          getRetailerStats(),
          // getRetailerStock({ page_size: 100 }),
          getRetailerVisualizerLink(),
        ]);

        if (statsResponse.success) {
          setStats(statsResponse.stats);
        }
        if (stockResponse.success) {
          setStockProducts(stockResponse.products || []);
        }

        // Populate global store with the retailer data payload
        if (visualizerResponse?.success && visualizerResponse?.data) {
          setRetailerData(visualizerResponse.data);
        }
      } catch (err) {
        console.error("Failed to fetch stats:", err);
        setError(err?.message || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [setRetailerData]);

  const lowStockProducts = stockProducts.filter((product) => product.is_low_stock);
  const outOfStockProducts = stockProducts.filter((product) => product.is_out_of_stock);
  const totalStockUnits = stockProducts.reduce(
    (total, product) => total + Number(product.stock_quantity || 0),
    0
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <RetailerSidebar />

      <main className="flex-1 ml-64 p-8">
        <div className="space-y-6">
          <DigitalShowroomCard visualizerToken={visualizerToken} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="flex flex-col h-full">
              <VisualizerLinkCard />
            </div>

            <div
              className="rounded-xl border p-6 shadow-sm flex flex-col justify-between"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
              }}
            >
              <div>
                <h3 className="text-lg font-semibold mb-1" style={{ color: colors.t1 }}>
                  Product Management
                </h3>
                <p className="text-sm mb-6" style={{ color: colors.t2 }}>
                  Manage sizes and tiles for your visualizer.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => navigate("/retailer/product-sizes")}
                    className="flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200 border group hover:shadow-sm"
                    style={{
                      backgroundColor: colors.accent + "08",
                      borderColor: colors.border,
                    }}
                  >
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-colors"
                      style={{ backgroundColor: colors.accent + "18" }}
                    >
                      <svg
                        className="w-5 h-5"
                        style={{ color: colors.accent }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: colors.t1 }}>
                        Manage Size
                      </p>
                      <p className="text-xs truncate" style={{ color: colors.t2 }}>
                        Add and manage product sizes
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate("/retailer/tiles")}
                    className="flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200 border group hover:shadow-sm"
                    style={{
                      backgroundColor: colors.accent + "08",
                      borderColor: colors.border,
                    }}
                  >
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-colors"
                      style={{ backgroundColor: colors.accent + "18" }}
                    >
                      <svg
                        className="w-5 h-5"
                        style={{ color: colors.accent }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: colors.t1 }}>
                        Manage Tiles
                      </p>
                      <p className="text-xs truncate" style={{ color: colors.t2 }}>
                        Add and manage tile products
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <IntegrateRoomViz />
        </div>
      </main>
    </div>
  );
}